import { z } from 'zod'
import { productSummarySchema } from './catalog.ts'
import { currencySchema } from './money.ts'
import { priceTierSchema } from './pricing.ts'

/**
 * The buy side, which has two halves: reading the prices shops already publish,
 * and asking them for one when reading is not enough.
 *
 * Reading comes first (D24). Shops keep their own ladders in `seller_listings`, so
 * for anything on the shelf the desk can see the rate at the quantity it needs and
 * place the order there and then. A price check — admin asks N shops "what's your
 * price for 200 bags" — is for the rest: an item nobody has listed, an unusual
 * quantity, a rate somebody thinks they can beat.
 *
 * What a shop is shown is the entire anonymity boundary in one sentence: product,
 * quantity, delivery city, deadline. No customer, no other shop's price, no
 * indication of what the platform will sell it for (D4).
 */

export const SOURCING_STATUSES = ['open', 'closed', 'cancelled'] as const
export type SourcingStatus = (typeof SOURCING_STATUSES)[number]
export const sourcingStatusSchema = z.enum(SOURCING_STATUSES)

export const INVITE_STATUSES = ['invited', 'quoted', 'declined', 'expired'] as const
export type InviteStatus = (typeof INVITE_STATUSES)[number]

export const INVITE_STATUS_LABELS: Record<InviteStatus, string> = {
  invited: 'Waiting',
  quoted: 'Priced',
  declined: 'Passed',
  expired: 'No answer',
}

/** The quote itself, as the admin sees it — with the shop named. */
export const sellerQuoteSchema = z.object({
  id: z.string(),
  orgId: z.string(),
  orgName: z.string(),
  unitPriceMinor: z.number().int(),
  availableQty: z.number().int(),
  leadTimeDays: z.number().int(),
  note: z.string().nullable(),
  currency: currencySchema,
  createdAt: z.number().int(),
})
export type SellerQuote = z.infer<typeof sellerQuoteSchema>

export const sourcingInviteSchema = z.object({
  id: z.string(),
  orgId: z.string(),
  orgName: z.string(),
  status: z.enum(INVITE_STATUSES),
  quote: sellerQuoteSchema.nullable(),
  invitedAt: z.number().int(),
})
export type SourcingInvite = z.infer<typeof sourcingInviteSchema>

export const sourcingRequestSchema = z.object({
  id: z.string(),
  code: z.string(),
  product: productSummarySchema,
  qty: z.number().int(),
  status: sourcingStatusSchema,
  note: z.string().nullable(),
  deliveryCity: z.string().nullable(),
  dueAt: z.number().int().nullable(),
  rfqId: z.string().nullable(),
  /**
   * The customer order this price check traces to, once the request behind it has
   * been won. It is what lets a shop order raised from here be recorded against
   * that order's supply worksheet instead of floating free.
   */
  order: z.object({ id: z.string(), code: z.string() }).nullable(),
  invites: z.array(sourcingInviteSchema),
  /** Cheapest quote in, for the comparison header. */
  bestUnitPriceMinor: z.number().int().nullable(),
  createdAt: z.number().int(),
  version: z.number().int(),
})
export type SourcingRequest = z.infer<typeof sourcingRequestSchema>

/**
 * A shop's view of the same row. Note what is absent and cannot be added by
 * accident: there is no buyer field, no rfqId, and no sibling quote.
 */
export const sourcingTaskSchema = z.object({
  id: z.string(),
  code: z.string(),
  product: productSummarySchema,
  qty: z.number().int(),
  deliveryCity: z.string().nullable(),
  note: z.string().nullable(),
  dueAt: z.number().int().nullable(),
  status: z.enum(INVITE_STATUSES),
  myQuote: z
    .object({
      unitPriceMinor: z.number().int(),
      availableQty: z.number().int(),
      leadTimeDays: z.number().int(),
      note: z.string().nullable(),
      createdAt: z.number().int(),
    })
    .nullable(),
  invitedAt: z.number().int(),
})
export type SourcingTask = z.infer<typeof sourcingTaskSchema>

// ---------------------------------------------------------------------------
// What shops already charge (D24)
// ---------------------------------------------------------------------------

/**
 * One shop's standing price for an item, resolved at the quantity we need.
 *
 * A price check asks; this reads. Everything here already exists on the shop's own
 * listing — the desk is not waiting on anybody, so the row carries what it takes to
 * decide and act: the rate at this quantity, whether the quantity clears the shop's
 * minimum, how long they take, and a number to ring.
 *
 * Desk-facing, like every other shape that names a shop (D4). It is never served to
 * a customer, and the phone on it is the shop's, never the customer's.
 */
export const shopPriceSchema = z.object({
  listingId: z.string(),
  orgId: z.string(),
  orgName: z.string(),
  orgCity: z.string().nullable(),
  orgPhone: z.string().nullable(),
  /** Their rate at the quantity asked for. Null when the ladder is empty. */
  unitPriceMinor: z.number().int().nullable(),
  lineTotalMinor: z.number().int().nullable(),
  tiers: z.array(priceTierSchema),
  moq: z.number().int(),
  /** The quantity asked for is under this shop's minimum order — a warning, not a bar. */
  belowMoq: z.boolean(),
  leadTimeDays: z.number().int(),
  /** What the shop says it holds, when it says at all. */
  stockQty: z.number().int().nullable(),
  sellerSku: z.string().nullable(),
  note: z.string().nullable(),
  currency: currencySchema,
  updatedAt: z.number().int(),
})
export type ShopPrice = z.infer<typeof shopPriceSchema>

export const shopPricesQuerySchema = z.object({
  productId: z.string().min(1, 'Pick an item.'),
  qty: z.coerce.number().int().min(1, 'Enter a quantity.'),
})
export type ShopPricesQuery = z.infer<typeof shopPricesQuerySchema>

export const createSourcingSchema = z.object({
  productId: z.string().min(1, 'Pick an item.'),
  qty: z.number().int().min(1, 'Enter a quantity.'),
  orgIds: z.array(z.string().min(1)).min(1, 'Pick at least one shop.').max(20),
  deliveryCity: z.string().trim().max(80).nullish(),
  note: z.string().trim().max(500).nullish(),
  /** Hours from now. */
  dueInHours: z.number().int().min(1).max(336).default(24),
  rfqId: z.string().max(64).nullish(),
})
export type CreateSourcingInput = z.infer<typeof createSourcingSchema>

export const submitQuoteSchema = z.object({
  unitPriceMinor: z.number().int().min(1, 'Enter your rate.'),
  availableQty: z.number().int().min(0),
  leadTimeDays: z.number().int().min(0).max(365).default(0),
  note: z.string().trim().max(500).nullish(),
})
export type SubmitQuoteInput = z.infer<typeof submitQuoteSchema>
